import { API_BASE, createFetchOptions } from './config'

export interface MediaUploadRequest {
  type: 'thumbnail' | 'preview'
  format: 'mp4' | 'gif' | 'jpg' | 'png'
  fileSize: number
}

export interface MediaUploadResponse {
  uploadUrl: string
  method: string
  filePath: string
  headers: Record<string, string>
  expiresAt: string
}

export interface MediaUpdateRequest {
  thumbnailPath?: string
  previewVideoPath?: string
}

export const mediaApi = {
  // Get presigned URL for upload
  async getUploadUrl(modelId: string, data: MediaUploadRequest, token?: string): Promise<MediaUploadResponse> {
    const headers: HeadersInit = {}
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    
    const response = await fetch(`${API_BASE}/api/v2/admin/models/${modelId}/media/upload`, 
      createFetchOptions({
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      })
    )

    const result = await response.json()
    if (!result.success) {
      throw new Error(result.error || 'Failed to get upload URL')
    }
    return result.data
  },

  // Upload file to R2 using presigned URL
  async uploadFile(uploadUrl: string, file: Blob, headers: Record<string, string>): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers,
    })

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`)
    }
  },

  // Update model with media paths
  async updateModelMedia(modelId: string, data: MediaUpdateRequest, token?: string): Promise<void> {
    const headers: HeadersInit = {}
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    
    const response = await fetch(`${API_BASE}/api/v2/admin/models/${modelId}/media`, 
      createFetchOptions({
        method: 'PUT',
        headers,
        body: JSON.stringify(data),
      })
    )

    const result = await response.json()
    if (!result.success) {
      throw new Error(result.error || 'Failed to update model media')
    }
  },

  // Helper function to upload media file
  async uploadMedia(
    modelId: string,
    file: Blob,
    type: 'thumbnail' | 'preview',
    format: 'mp4' | 'gif' | 'jpg' | 'png',
    token?: string,
    onProgress?: (progress: number) => void
  ): Promise<string> {
    // Direct upload through backend proxy to avoid CORS issues
    const url = `${import.meta.env.VITE_API_URL}/api/v2/admin/models/${modelId}/media/upload?type=${type}&format=${format}`
    
    let filePath: string

    // Upload file
    if (onProgress) {
      // For progress tracking, we'd use XMLHttpRequest
      const response = await new Promise<{ filePath: string }>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const progress = (e.loaded / e.total) * 100
            onProgress(progress)
          }
        })

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const result = JSON.parse(xhr.responseText)
              resolve(result.data)
            } catch (error) {
              reject(new Error('Invalid response'))
            }
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText)
              reject(new Error(`Upload failed: ${errorData.error || xhr.statusText}${errorData.details ? ` - ${errorData.details}` : ''}`))
            } catch {
              reject(new Error(`Upload failed: ${xhr.statusText}`))
            }
          }
        })

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed'))
        })

        xhr.open('PUT', url)
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`)
        }
        xhr.send(file)
      })
      
      filePath = response.filePath
    } else {
      const headers: HeadersInit = {}
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
      
      const response = await fetch(url, 
        createFetchOptions({
          method: 'PUT',
          headers,
          body: file,
        })
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(`Upload failed: ${errorData.error || response.statusText}${errorData.details ? ` - ${errorData.details}` : ''}`)
      }

      const result = await response.json()
      filePath = result.data.filePath
    }

    // Update model with new media path
    const updateData: MediaUpdateRequest = {}
    if (type === 'thumbnail') {
      updateData.thumbnailPath = filePath
    } else {
      updateData.previewVideoPath = filePath
    }
    
    await this.updateModelMedia(modelId, updateData, token)

    return filePath
  },
}